<template>
  <v-navigation-drawer v-model="isDrawerOpen" location="right" temporary :width="xs ? 350 : 422">
    <div class="d-flex flex-column pa-4 h-100 overflow-y-auto">
      <div class="d-flex justify-space-between align-center mb-4">
        <h3>{{ t("components.transactions.filters.title") }}</h3>
        <v-btn variant="text" icon="mdi-window-close" @click.stop="isDrawerOpen = !isDrawerOpen" />
      </div>
      <div class="d-flex flex-column ga-2 flex-grow-1">
        <div class="d-flex flex-column ga-2">
          <VueDatePicker
            v-model="dates"
            :locale="locale"
            :cancel-text="t('form.cancel')"
            :select-text="t('form.select')"
            format="dd/MM/yyyy"
            :placeholder="t('components.transactions.filters.dateRange.label')"
            :range="{ noDisabledRange: true }"
            :max-date="new Date()"
            :enable-time-picker="false"
            :preset-dates="datePickerPresetRanges"
            position="right"
            class="mb-4"
          />
          <p v-if="salesDayClosingTime" class="text-body-2 px-1 mb-5">
            <v-icon>mdi-timer-refresh-outline</v-icon>
            {{
              t("components.transactions.filters.dateRange.tradingHours", {
                hours: formatMerchantHours(salesDayClosingTime),
              })
            }}
          </p>
          <v-autocomplete
            v-model="statuses"
            multiple
            :label="t('components.transactions.filters.status.label')"
            :items="
              Object.values(TransactionStatusEnum).map((status) => ({
                title: t(`transactions.status.${status}`),
                value: status,
              }))
            "
            clearable
          />
          <v-autocomplete
            v-model="paymentSource"
            :label="t('components.transactions.filters.paymentSource.label')"
            :items="
              Object.values(PaymentSource)
                .filter((source) => source !== PaymentSource.TapToPay || isTTPEnabled)
                .map((source) => ({
                  title: t(`components.transactions.filters.paymentSource.${source}`),
                  value: source,
                }))
            "
            clearable
          />
          <v-autocomplete
            v-model="paymentMethods"
            multiple
            :label="t('components.transactions.filters.paymentMethod.label')"
            :items="
              Object.values(AddSitesPaymentMethodRequestPaymentMethodEnum).map((method) => ({
                title: getPaymentMethodPublicName(method),
                value: method,
              }))
            "
            clearable
          />
          <v-text-field
            v-model="pspReference"
            :label="t('components.transactions.filters.pspReference.label')"
            clearable
            @input="() => (pspReference = pspReference?.toUpperCase())"
          />
          <v-text-field
            v-model="cardSummary"
            clearable
            :label="t('components.transactions.filters.cardSummary.label')"
            :error-messages="
              cardSummary && cardSummary.toString().length !== 4
                ? t('components.transactions.filters.cardSummary.error')
                : ''
            "
          />

          <v-btn variant="outlined" class="mt-3 mb-2" @click="handleResetBtn">
            {{ t("components.transactions.filters.actions.reset") }}
          </v-btn>
          <v-btn @click="handleDoneBtn">
            {{ t("components.transactions.filters.actions.done") }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import {
  AddSitesPaymentMethodRequestPaymentMethodEnum,
  PaymentSource,
  TransactionStatusEnum,
} from "@tableyeti/merchant-service";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { PostHog } from "posthog-js";
import { inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useMerchantsStore } from "@/store/merchants";
import { useTransactionsStore } from "@/store/transactions.ts";
import { getPaymentMethodPublicName } from "@/utils/format.ts";

const { t, locale } = useI18n();
const { xs } = useDisplay();

const posthog = inject<PostHog>("posthog");
const isTTPEnabled = posthog?.isFeatureEnabled("tap-to-pay") ?? false;

const { transactionsParams } = useTransactionsStore();

const merchantsStore = useMerchantsStore();

const pspReference = ref<string | undefined>(transactionsParams.pspReference);
const dates = ref<Date[]>(transactionsParams.dates || []);
const paymentMethods = ref<AddSitesPaymentMethodRequestPaymentMethodEnum[] | undefined>(
  transactionsParams.paymentMethods,
);
const statuses = ref<TransactionStatusEnum[] | undefined>(transactionsParams.statuses);
const paymentSource = ref<PaymentSource | undefined>(transactionsParams.paymentSource || undefined);
const cardSummary = ref<number | undefined>(transactionsParams.cardSummary || undefined);

watch(
  () => transactionsParams,
  () => {
    paymentSource.value = transactionsParams.paymentSource || undefined;
    pspReference.value = transactionsParams.pspReference || undefined;
    dates.value = transactionsParams.dates || [];
    cardSummary.value = transactionsParams.cardSummary || undefined;
    statuses.value = transactionsParams.statuses || undefined;
    paymentMethods.value = transactionsParams.paymentMethods || undefined;
  },
  { deep: true },
);

const isDrawerOpen = defineModel<boolean>();
const salesDayClosingTime = merchantsStore.currentMerchant?.settings?.salesDayClosingTime;

const startOfDayToday = startOfDay(new Date());
const endOfDayToday = endOfDay(new Date());

const datePickerPresetRanges = ref([
  {
    label: t("components.transactions.filters.dateRange.presets.today"),
    value: [startOfDayToday, endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.yesterday"),
    value: [subDays(startOfDayToday, 1), subDays(endOfDayToday, 1)],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisWeek"),
    value: [startOfWeek(startOfDayToday, { weekStartsOn: 1 }), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.lastWeek"),
    value: [
      startOfWeek(subWeeks(startOfDayToday, 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(endOfDayToday, 1), { weekStartsOn: 1 }),
    ],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisMonth"),
    value: [startOfMonth(startOfDayToday), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.lastMonth"),
    value: [startOfMonth(subMonths(startOfDayToday, 1)), endOfMonth(subMonths(endOfDayToday, 1))],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.last90Days"),
    value: [subDays(startOfDayToday, 90), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.last6Months"),
    value: [startOfMonth(subMonths(startOfDayToday, 6)), endOfMonth(subMonths(endOfDayToday, 1))],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisYear"),
    value: [startOfYear(startOfDayToday), endOfDayToday],
  },
]);

const handleResetBtn = () => {
  pspReference.value = undefined;
  dates.value = [startOfDayToday, endOfDayToday];
  cardSummary.value = undefined;
  statuses.value = undefined;
  paymentMethods.value = undefined;
  paymentSource.value = undefined;
};

const formatMerchantHours = (hours: string) => {
  const hour = hours.split(":")[0].replace("0", "");
  return `${hour}am - ${hour}am`;
};

const handleDoneBtn = () => {
  if (!/[a-z]/.exec(pspReference.value ?? "")) {
    // To set back today date when pspReference is empty for UX
    if (!pspReference.value && !transactionsParams.dates) {
      transactionsParams.dates = [startOfDay(new Date()), endOfDay(new Date())];
    }
    transactionsParams.pspReference = pspReference.value;
  }

  if (transactionsParams.dates !== dates.value) {
    transactionsParams.dates = dates.value?.filter((date) => !!date);
  }
  if (transactionsParams.cardSummary !== cardSummary.value) {
    transactionsParams.cardSummary = cardSummary.value || undefined;
  }
  if (transactionsParams.statuses !== statuses.value) {
    transactionsParams.statuses = statuses.value || undefined;
  }
  if (transactionsParams.paymentMethods !== paymentMethods.value) {
    transactionsParams.paymentMethods = paymentMethods.value || undefined;
  }
  if (transactionsParams.paymentSource !== paymentSource.value) {
    transactionsParams.paymentSource = paymentSource.value || undefined;
  }

  isDrawerOpen.value = !isDrawerOpen.value;
};
</script>

<style scoped>
.v-navigation-drawer :deep(.v-navigation-drawer__content) {
  overflow: visible !important;
}
</style>
